import React from 'react'
import PropTypes from 'prop-types'

import { TextFormEntry, StateInput } from '../../components'

import { AddressShape } from './propTypes'

import { logError } from '../UserMessaging'
import { TranslationUtility } from "../../../util/translationUtility";


const requiredFields = ["name", "line1", "country", "city", "state", "postalCode"];
const makeCountryNameLookup = (countries => countryCode => {
    const pair = countries.filter((c) =>  c.Code === countryCode)[0];
    if (pair && pair.Name)
        return pair.Name;
    return '';
});
const defaultCountryCode = "USA";
const zipRe = /(^\d{5})|(^\d{5})-\d{4}$/;
const extractFipsLookupZipCode = (postalCode) => {
    var match = zipRe.exec(postalCode);
  if (match) {
      return match[1];
  }
  return '';
}
const isValidFipsItem = fipsOption => typeof fipsOption.Value === 'string' && typeof fipsOption.Text === 'string';

export class AddressEditor extends React.Component {
  constructor(props) {
    super(props);
    
    this.getCountryName = makeCountryNameLookup(this.props.countries);

    const newAddress = {
      AddressLine1: "",
      AddressLine2: "",
      AddressType: props.addressType,
      City: "",
      CountryCode: defaultCountryCode,
      CountryName: this.getCountryName(defaultCountryCode),
      FipsCode: "",
      Id: "",
      Name: "",
      Phone: "",
      PostalCode: "",
      State: ""
    };
    const initialAddress = props.address || Object.assign({}, newAddress);
    this.state = {
      address: initialAddress,
      submitAttempted: false,
      countriesStatesMap: props.countriesStatesMap,
      invalidFields: {},
      originalId: props.address && props.address.Id,
      validationRun: false,
      lastFipsZip: initialAddress.PostalCode,
      fipsOptions: []
    };
    
    this.handleChange = this.handleChange.bind(this);
    this.handleCountryChange = this.handleCountryChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.validateField = this.validateField.bind(this);
    this.isInvalid = this.isInvalid.bind(this);
    this.isRequired = this.isRequired.bind(this);
    this.updateFips = this.updateFips.bind(this);
    this.translationUtility = new TranslationUtility(props.localizationItems);
  }

  updateFips(zipCode) {
      if (this.state.address.CountryCode === "USA") {
      const zip = extractFipsLookupZipCode(zipCode);
      const self = this;
      if (zip) {
        if (this.state.lastFipsZip !== zip) {
          this.setState({ lastFipsZip: zip });
          fetch(this.props.fipsEndpoint + zip).then(response => {
              if (response.ok)
                  return response.json();
              console.log("Network error fetching fips from zip " + zip)
          }).then(json => {
                  if (Array.isArray(json)) {
                      self.setState({
                          fipsOptions: json ,
                          address: Object.assign({}, self.state.address, { FipsCode: json[0].Value || '' }) });
                  }
                  return;
              }
          ).catch(error => logError(error));
        }
      } else {
        this.setState({ lastFipsZip: '' });
      }
    }
  }

  isRequired(name) {
    return requiredFields.indexOf(name) > -1;
  }

  validateField(invalidFields, fieldName, value) {
    if (this.isInvalid(value, fieldName)) {
      invalidFields[fieldName] = true;
    } else {
      invalidFields[fieldName] = false;
    }
  }

  handleChange(event) {
    const address = Object.assign({}, this.state.address);
    const value = event.target.value;
    const name = event.target.name;
    switch (name) {
      case "name":
        address.Name = value;
        break;
      case "line1":
        address.AddressLine1 = value;
        break;
      case "line2":
        address.AddressLine2 = value;
        break;
      case "city":
        address.City = value;
        break;
      case "state":
        address.State = value;
        break;
      case "postalCode":
        address.PostalCode = value;
        this.updateFips(value);
        break;
      case "phone":
        address.Phone = value;
        break;
      case "fips":
        address.FipsCode = value;
        break;
      default:
        if (typeof console.warn !== 'undefined')
          console.warn(`Unknown value change with name '${name}' and value '${value}'`);
    }
    const invalidFields = Object.assign({}, this.state.invalidFields);
    this.validateField(invalidFields, name, value);
    this.setState({ address: address, invalidFields: invalidFields });
  }

  handleCountryChange(event) {
      const countryCode = event.target.value;
      const countryWithCode = this.getCountryName(countryCode);
    if (countryWithCode.length > 0) {
      this.setState({
        address: Object.assign({},
          this.state.address,
          {
            CountryCode: countryCode,
            CountryName: countryWithCode[0].Code
          })
      });
    }
    event.preventDefault();
  }

  onSubmit(event) {
    event.preventDefault();
    if (!event.target.checkValidity()) {
      return;
    }
    const keys = this.state.invalidFields ? Object.keys(this.state.invalidFields) : [];
    const invalidFieldNames = keys.filter((key) => this.state.invalidFields[key]);
    let errorMessage = "";
    if (invalidFieldNames.length > 0) {
      errorMessage = `Please fix the following fields: ${invalidFieldNames.join(", ") + "."}`;
      this.setState({ errorMessage: errorMessage });
    } else {
      this.props.onEditOk(this.state.address);
    }
  }
  
  isInvalid(value, inputName) {
    if ((!value || value.length < 1) && this.isRequired(inputName)) {
      return true;
    }
    return false;
  }
  
  render() {
    const selectedAddress = this.state.address;
    const countryOptions = this.props.countries.map((country) =>
      <option key={country.Code} value={country.Code}>{country.Name}</option>
    );
    const fipsArray = Array.isArray(this.state.fipsOptions)
        ? this.state.fipsOptions
        : [];
    return (
      <form className="addressEditor" onSubmit={this.onSubmit} style={{margin: 17}}>
          {this.state.submitted && this.state.errorMessage &&
              <div className="alert alert-danger" role="alert">
                {this.state.errorMessage}
              </div>
            }
          <TextFormEntry name={'name'} value={selectedAddress.Name} label={this.translationUtility.translateResourceString("/Checkout/Address/Name")}
                         onChange={this.handleChange} isInvalid={this.isInvalid} isRequired={this.isRequired}
          />
            <TextFormEntry name={'line1'} label={this.translationUtility.translateResourceString("/Checkout/Address/Address1")} value={selectedAddress.AddressLine1}
                         onChange={this.handleChange} isInvalid={this.isInvalid} isRequired={this.isRequired}
                         autocomplete={'address-line1'}
          />
            <TextFormEntry name={'line2'} label={this.translationUtility.translateResourceString("/Checkout/Address/Address2")} value={selectedAddress.AddressLine2}
                         onChange={this.handleChange} isInvalid={this.isInvalid} isRequired={this.isRequired}
                         autocomplete={'address-line'}
          />
          <div className={`${this.isInvalid("country") ? "error" : ""} form-group row`}>
                <label htmlFor="country" className="col-sm-3 control-label d-none d-sm-inline">* {this.translationUtility.translateResourceString("/Checkout/Address/Country")}:</label>
              <div className="col-sm-9">
                  <select 
                      value={selectedAddress.CountryCode}
                      onChange={this.handleCountryChange}
                      required
                      className=" form-control"
                  >
                      {countryOptions}
                  </select>
              </div>
          </div>
            <TextFormEntry name={'city'} value={selectedAddress.City} label={this.translationUtility.translateResourceString("/Checkout/Address/City")} 
                         onChange={this.handleChange} isInvalid={this.isInvalid} isRequired={this.isRequired}
                         autocomplete={'address-level2'}
          />
          <div className={`form-group row ${(this.state.address.State === '') ? "error" : ""}`}>
                <label htmlFor="state" className="col-sm-3 control-label d-none d-sm-inline">* {this.translationUtility.translateResourceString("/Checkout/Address/State")}:</label>
              <div className="col-sm-9">
                  <StateInput 
                      CountryCode={selectedAddress.CountryCode}
                      value={selectedAddress.State}
                      countriesStatesMap={this.state.countriesStatesMap}
                      onChange={this.handleChange}
                      isRequired={this.isRequired}
                  />
              </div>
          </div>
            <TextFormEntry name='postalCode' value={selectedAddress.PostalCode} label={this.translationUtility.translateResourceString("/Checkout/Address/PostalCode")}
                         onChange={this.handleChange} isInvalid={this.isInvalid} isRequired={this.isRequired}
                         autocomplete={'postal-code'}
          />
            <TextFormEntry type='tel' name={'phone'} value={selectedAddress.Phone} label={this.translationUtility.translateResourceString("/Checkout/Address/Phone")} 
                         onChange={this.handleChange} isInvalid={this.isInvalid} isRequired={this.isRequired}
                         autocomplete={'tel'}
          />
          <div className={`${fipsArray.length > 1 ? '' : 'd-none'} form-group row`}>
                <label htmlFor="fips" className="col-sm-3 control-label d-none d-sm-inline"> {this.translationUtility.translateResourceString("/Checkout/Address/Jurisdiction")}:</label>
              <div className="col-sm-9">
                  <select name='fips'
                          value={selectedAddress.FipsCode || ''}
                          onChange={this.handleChange}
                          className="form-control"
                  >
                      {fipsArray.map(opt => 
                          isValidFipsItem(opt) && 
                          <option key={opt.Value} value={opt.Value}>{opt.Text}</option>)}
                  </select>
              </div>
          </div>
          <div className="form-group">
              <div className="col-sm-offset-2 col-sm-10">
                    <button type="submit" className="btn btn-primary" style={{ marginLeft: 20 }} disabled="">{this.translationUtility.translateResourceString("/Checkout/Address/Save")}</button>
                    <button type="button" className="btn btn-default" style={{ marginLeft: 20 }} onClick={this.props.onEditCancel} value="Cancel">{this.translationUtility.translateResourceString("/Checkout/Address/Cancel")}</button>
              </div>
          </div>
      </form>

    );
  }
}

AddressEditor.propTypes = {
  addressType: PropTypes.number.isRequired,
  address: AddressShape.propTypes,
  countriesStatesMap: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  onEditCancel: PropTypes.func.isRequired,
  onEditOk: PropTypes.func.isRequired,
  localizationItems: PropTypes.array.isRequired
}

export default AddressEditor


// WEBPACK FOOTER //
// ./src/Components/checkout/Features/Address/AddressEditor.jsx